import { useCallback, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import FastForwardOutlinedIcon from '@mui/icons-material/FastForwardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NRUserIcon from '../../../../NRIcons/NRUserIcon';
import NRVulnVoltIcon from '../../../../NRIcons/NRVulnVoltIcon';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import NRGenericIcon from '../../../../NRIcons/NRPackageManagerIcons/NRGenericIcon';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PhotoSizeSelectActualOutlinedIcon from '@mui/icons-material/PhotoSizeSelectActualOutlined';
import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react';
import { useSnackbar } from 'notistack';
import { CONSTANTS } from '../../../../constants/constants';
import { capitalizeFirstLetter, indexToLetters } from '../../../../utils/utils';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import NRShortenedPathTooltip from '../../../NRReactGridCustomComponents/NRCustomProviders/NRShortenFilePath/NRShortenedPathTooltip';
import CardContent from '@mui/material/CardContent';
import NRNoDataPresentComponent from '../../../NRNoDataPresentComponent/NRNoDataPresentComponent';

const ReachabilityItem = ({
  subtitle,
  title,
  cveId,
  invocation,
  icon,
  endIcon,
  endText,
  showConnector,
  showVerticalConnector,
  marginValue,
  hideEmptyInvocation
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState();
  return (
    <Card
      elevation={0}
      sx={{
        p: 0,
        background: theme.palette.background.default,
        overflow: 'visible'
      }}
    >
      <CardHeader
        sx={{
          ml: marginValue,
          py: 0.5,
          px: 0.5,
          '& .MuiCardHeader-action': {
            m: 'unset',
            alignSelf: 'center'
          },
          position: 'relative',
          '&::before': {
            content: showConnector ? '""' : 'none',
            position: 'absolute',
            left: showVerticalConnector ? 24 : -8,
            top: -18,
            width: 12,
            height: 46,
            borderLeft: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderBottomLeftRadius: 4
          }
        }}
        avatar={
          <Avatar
            variant={'circular'}
            sx={theme => ({
              width: 48,
              height: 48,
              boxShadow: theme.shadows[3],
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.iconInfo
            })}
          >
            {icon}
          </Avatar>
        }
        action={
          !hideEmptyInvocation || !!invocation ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={invocation ? { textDecoration: 'underline', cursor: 'pointer' } : { cursor: 'default' }}
                onClick={invocation ? () => setExpanded(prev => !prev) : undefined}
                color="text.secondary"
                children={endText || cveId}
              />
              <Box sx={{ marginLeft: 2 }}>{endIcon}</Box>
            </Box>
          ) : undefined
        }
        title={title}
        subheader={subtitle}
      />
      <Collapse
        in={invocation && expanded}
        timeout="auto"
        unmountOnExit={!invocation}
        sx={theme => ({
          position: 'relative',
          '&::before': {
            content: showConnector ? '""' : 'none',
            position: 'absolute',
            left: 56,
            top: -4,
            width: '1px',
            height: '100%',
            borderLeft: `1px solid ${theme.palette.divider}`
          }
        })}
      >
        <SyntaxHighlighter
          customStyle={{ margin: '16px 0 16px 96px', backgroundColor: theme.palette.neutral[800], border: `1px solid ${theme.palette.divider}` }}
          showLineNumbers={false}
          wrapLongLines={true}
          language="text"
          style={a11yDark}
        >
          {invocation}
        </SyntaxHighlighter>
      </Collapse>
    </Card>
  );
};

function ReachabilityRow({ item, index, componentName, isLoading, severityColor, handleCopy }) {
  const theme = useTheme();
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef });

  return (
    <Box key={`${index}-box-container`} sx={{ '&:last-child': { mb: 3 }, px: 0.5 }}>
      <Box mt={2} mb={1} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Box>
          <Typography variant={'subtitle2'} color={'textSecondary'}>{`Exhibit ${indexToLetters(index)}`}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton aria-label="Copy" sx={{ padding: 0.75 }} onClick={() => handleCopy(item)} size="large">
            <ContentCopyOutlinedIcon fontSize="medium" />
          </IconButton>
          <IconButton aria-label="Copy Image" sx={{ marginLeft: 1, padding: 0.75 }} onClick={handlePrint} size="large">
            <PhotoSizeSelectActualOutlinedIcon fontSize="medium" />
          </IconButton>
        </Box>
      </Box>
      <Paper
        ref={contentRef}
        elevation={0}
        sx={theme => ({
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.default,
          p: 2
        })}
      >
        {isLoading ? (
          <Box
            sx={{
              minHeight: 180,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ position: 'relative' }}>
            {/*Entry point: Service / cron */}
            <ReachabilityItem
              user={item.user}
              title={<NRShortenedPathTooltip filePath={item.entryPoint} maxWidth={250} />}
              subtitle={capitalizeFirstLetter(item.entryType)}
              key={`${index}-${item.entryPoint}`}
              showConnector={false}
              showVerticalConnector={false}
              icon={<FastForwardOutlinedIcon sx={{ width: 32, height: 32 }} />}
              endIcon={<NRUserIcon width={24} height={24} />}
              endText={`As ${item.user}`}
            />
            {/*Binaries*/}
            {item?.scripts?.map((script, scriptIndex) => (
              <ReachabilityItem
                title={script?.path?.split('/')?.pop()}
                subtitle={'Binary'}
                hideEmptyInvocation={true}
                invocation={script?.invocation}
                key={`${index}-${script?.path?.split('/')?.pop()}`}
                showConnector={true}
                showVerticalConnector={scriptIndex !== 0}
                icon={<DescriptionOutlinedIcon sx={{ width: 32, height: 32 }} />}
                endIcon={<CodeIcon width={24} height={24} />}
                endText={`View Invocation`}
                marginValue={4}
              />
            ))}
            {/*CVE*/}
            <ReachabilityItem
              cveId={item.cveId}
              title={componentName}
              subtitle={'Component'}
              key={`${index}-${item.cveId}`}
              showConnector={true}
              showVerticalConnector={false}
              icon={<NRGenericIcon width={32} height={32} color={theme.palette.text.iconInfo} />}
              endIcon={<NRVulnVoltIcon width={24} height={24} color={severityColor} />}
              marginValue={8}
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

const ReachabilityComponent = ({ componentName, reachabilityData, isLoading, severityColor }) => {
  const { enqueueSnackbar } = useSnackbar();
  const handleCopy = useCallback(item => {
    const json = JSON.stringify(item, (key, value) => (key === '__typename' ? undefined : value), 2);
    navigator.clipboard.writeText(json);
    enqueueSnackbar(CONSTANTS.COPIED_TO_CLIPBOARD_MSG, { variant: 'success', preventDuplicate: true, autoHideDuration: 1000 });
  }, []);

  const LIST_HEIGHT = reachabilityData?.length > 1 ? 520 : 440;

  return (
    <>
      {reachabilityData?.length > 1 && (
        <Box sx={{ maxHeight: LIST_HEIGHT, overflow: 'hidden' }}>
          <Virtuoso
            style={{ height: LIST_HEIGHT }}
            data={reachabilityData}
            overscan={300}
            itemContent={(index, item) => (
              <ReachabilityRow
                item={item}
                index={index}
                componentName={componentName}
                isLoading={isLoading}
                severityColor={severityColor}
                handleCopy={handleCopy}
              />
            )}
          />
        </Box>
      )}
      {reachabilityData?.length === 1 && (
        <ReachabilityRow
          item={reachabilityData[0]}
          index={0}
          componentName={componentName}
          isLoading={isLoading}
          severityColor={severityColor}
          handleCopy={handleCopy}
        />
      )}
      {!!isLoading && (
        <CardContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <CircularProgress data-testid="circular-progress" />
        </CardContent>
      )}
      {!isLoading && !reachabilityData?.length && (
        <Box display="flex" justifyContent="center" alignItems="center" height="inherit" mt={1}>
          <NRNoDataPresentComponent />
        </Box>
      )}
    </>
  );
};

export default ReachabilityComponent;
